import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'

type AuthMode = 'signin' | 'signup' | 'forgot'

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    user,
    loading,
    error,
    signInWithEmail,
    signUpWithEmail,
    requestPasswordReset,
    initialize,
    clearError,
  } = useAuthStore()

  const returnTo = searchParams.get('returnTo') || '/'

  const [mode, setMode] = useState<AuthMode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  // Initialize auth on mount.
  useEffect(() => {
    initialize()
  }, [initialize])

  // Redirect if already authenticated.
  useEffect(() => {
    if (user) {
      navigate(returnTo, { replace: true })
    }
  }, [user, navigate, returnTo])

  const switchMode = (nextMode: AuthMode) => {
    clearError()
    setMessage(null)
    setPassword('')
    setConfirmPassword('')
    setMode(nextMode)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)

    if (mode === 'signin') {
      await signInWithEmail(email, password)
    } else if (mode === 'signup') {
      if (password !== confirmPassword) {
        return
      }
      const ok = await signUpWithEmail(email, password)
      if (ok) {
        setMessage('Check your email for a confirmation link')
      }
    } else if (mode === 'forgot') {
      await requestPasswordReset(email)
      if (!error) {
        setMessage('Check your email for a password reset link')
      }
    }
  }

  const isFormValid = () => {
    if (!email) return false
    if (mode === 'forgot') return true
    if (!password) return false
    if (mode === 'signup' && password !== confirmPassword) return false
    return true
  }

  return (
    <div className="h-dvh flex flex-col items-center justify-center bg-background px-6">
      {/* Brand */}
      <div className="mb-12 text-center">
        <h1 className="text-2xl font-light tracking-tight text-foreground">factory</h1>
        <p className="mt-2 text-sm text-muted-foreground font-light">Sign in to continue</p>
      </div>

      {/* Login card */}
      <div className="w-full max-w-sm">
        {/* Error state */}
        {error && (
          <div className="mb-6 p-4 border border-destructive/20 rounded-md bg-destructive/5">
            <p className="text-sm text-destructive">{error}</p>
            <button
              onClick={clearError}
              className="mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Success message */}
        {message && (
          <div className="mb-6 p-4 border border-foreground/20 rounded-md bg-foreground/5">
            <p className="text-sm text-foreground">{message}</p>
          </div>
        )}

        {/* Email/password form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="sr-only">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              autoComplete="email"
              className="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm font-light focus:outline-none focus:ring-1 focus:ring-foreground"
              style={{ minHeight: '44px' }}
            />
          </div>

          {mode !== 'forgot' && (
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                className="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm font-light focus:outline-none focus:ring-1 focus:ring-foreground"
                style={{ minHeight: '44px' }}
              />
            </div>
          )}

          {mode === 'signup' && (
            <div>
              <label htmlFor="confirmPassword" className="sr-only">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                autoComplete="new-password"
                className={`w-full px-4 py-2.5 rounded-md border bg-background text-foreground placeholder:text-muted-foreground text-sm font-light focus:outline-none focus:ring-1 focus:ring-foreground ${
                  confirmPassword && password !== confirmPassword
                    ? 'border-destructive'
                    : 'border-border'
                }`}
                style={{ minHeight: '44px' }}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-xs text-destructive">Passwords do not match</p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !isFormValid()}
            className="w-full flex items-center justify-center bg-foreground text-background hover:bg-foreground/90 px-4 py-2.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ minHeight: '44px' }}
          >
            <span className="text-sm font-light">
              {loading
                ? 'Please wait...'
                : mode === 'signin'
                  ? 'Sign in'
                  : mode === 'signup'
                    ? 'Create account'
                    : 'Send reset link'}
            </span>
          </button>
        </form>

        {/* Mode toggle links */}
        <div className="mt-4 text-center text-sm font-light text-muted-foreground">
          {mode === 'signin' && (
            <>
              <button
                onClick={() => switchMode('forgot')}
                className="hover:text-foreground transition-colors"
              >
                Forgot password?
              </button>
              <span className="mx-2">·</span>
              <button
                onClick={() => switchMode('signup')}
                className="hover:text-foreground transition-colors"
              >
                Create account
              </button>
            </>
          )}
          {mode === 'signup' && (
            <button
              onClick={() => switchMode('signin')}
              className="hover:text-foreground transition-colors"
            >
              Already have an account? Sign in
            </button>
          )}
          {mode === 'forgot' && (
            <button
              onClick={() => switchMode('signin')}
              className="hover:text-foreground transition-colors"
            >
              Back to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
